import { AsyncPipe } from "@angular/common";
import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import { combineLatest, Observable, shareReplay, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { TabComponent } from "~app/common/components/tabs/tab/tab.component";
import { TabsComponent } from "~app/common/components/tabs/tabs.component";
import { PermittedDirective } from "~app/common/directives/permitted/permitted.directive";
import { EntityType, Kostnadstyper, RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { canView } from "~app/role-permissions";
import { VkAtgardEntity } from "../../models/atgard-models";
import { Kontrakt } from "../../models/kontrakt-models";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { KontraktGenereraRapportComponent } from "../kontrakt-utskrift/kontrakt-generera-rapport/kontrakt-generera-rapport.component";
import { KontraktMatbeskedComponent } from "../kontrakt-utskrift/kontrakt-matbesked/kontrakt-matbesked.component";
@Component({
    selector: "rask-kontrakt-utskrift-dokument",
    templateUrl: "./kontrakt-utskrift-dokument.component.html",
    styleUrls: ["./kontrakt-utskrift-dokument.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        TabsComponent,
        TabComponent,
        KontraktGenereraRapportComponent,
        KontraktMatbeskedComponent,
        AsyncPipe,
        PermittedDirective
    ]
})
export class KontraktUtskriftDokumentComponent implements OnInit, OnDestroy {
    protected enableDocumentPanel$: Observable<boolean>;
    protected enableSkogsvardTab$: Observable<boolean>;
    protected enableRundvirkeGrotTab$: Observable<boolean>;
    protected canGenerateSkogsvardsKontrakt$: Observable<boolean>;
    protected canGenerateSkogsvardSammanstallning$: Observable<boolean>;
    protected canGenerateSkogsvardsKarta$: Observable<boolean>;
    protected canView = canView();

    protected kontrakt$: Observable<Kontrakt>;
    protected canGenerateMatbesked$: Observable<boolean>;
    protected loading = true;
    protected kontraktAtgarder$: Observable<[Kontrakt, VkAtgardEntity[]]>;
    protected icon$: Observable<string>;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly validationService = inject(VirkeskopValidationService);

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.kontrakt$ = this.kontraktMediatorService.kontrakt$;

        this.icon$ = this.kontrakt$.pipe(
            map(kontrakt => this.validationService.getEntitySeverity("utskrift", kontrakt?.guid))
        );

        this.canGenerateMatbesked$ = this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => kontrakt.viol3 && this.authenticationService.hasAnyRole([
                RaskRoller.role.Virkeskopa,
                RaskRoller.role.Kopleda
            ])),
            shareReplay(1)
        );

        this.kontraktAtgarder$ = combineLatest([
            this.kontraktMediatorService.kontrakt$,
            this.kontraktMediatorService.atgarderPreload$
        ]).pipe(shareReplay(1));

        this.enableDocumentPanel$ = this.getDocumentPanelVisibility();
        this.enableSkogsvardTab$ = this.getSkogsvardTabVisibility();
        this.enableRundvirkeGrotTab$ = this.getRundvirkeGrotTabVisibility();
        this.canGenerateSkogsvardsKontrakt$ = this.getCanGenerateSkogsvardsKontrakt();
        this.canGenerateSkogsvardSammanstallning$ = this.getCanGenerateSkogsvardSammanstallning();
        this.canGenerateSkogsvardsKarta$ = this.getCanGenerateSkogsvardsKarta();
    }

    private hasSkogsvardsatgardAndAccess(atgarder: VkAtgardEntity[]) {
        return this.hasSkogsvardAtgard(atgarder) && this.canView();
    }

    private getSkogsvardTabVisibility() {
        return this.kontraktAtgarder$.pipe(
            map(([
                kontrakt,
                atgarder
            ]) => kontrakt.showSkogsvard && this.hasSkogsvardAtgard(atgarder)
            ));
    }

    private getRundvirkeGrotTabVisibility() {
        return this.kontrakt$.pipe(
            map(kontrakt => !kontrakt?.isAnySkogsvard)
        );
    }

    private getDocumentPanelVisibility() {
        return this.kontraktAtgarder$.pipe(
            map(([
                kontrakt,
                atgarder
            ]) => !_.isNil(kontrakt.id) && atgarder.length > 0
            ));
    }

    private getCanGenerateSkogsvardsKontrakt() {
        return this.kontraktAtgarder$.pipe(
            map(([
                kontrakt,
                atgarder
            ]) => !kontrakt.isSkogsvardFPVKontrakt && this.hasSkogsvardOchDirektAvverkningskostnadAndAccess(atgarder, kontrakt)
            ));

    }

    private getCanGenerateSkogsvardSammanstallning() {
        return this.kontraktAtgarder$.pipe(
            map(([
                kontrakt,
                atgarder
            ]) => kontrakt.isSkogsvardFPVKontrakt && this.hasSkogsvardsatgardAndAccess(atgarder)
            ));
    }

    private getCanGenerateSkogsvardsKarta() {
        return this.kontraktMediatorService.atgarderPreload$.pipe(
            map(atgarder => this.hasSkogsvardsatgardAndAccess(atgarder))
        );
    }

    private hasSkogsvardAtgard(atgarder: VkAtgardEntity[]) {
        return atgarder.some(x => x.type === EntityType.virkeskopsAtgardSkogsvard);
    }

    private hasSkogsvardOchDirektAvverkningskostnadAndAccess(atgarder: VkAtgardEntity[], kontrakt: Kontrakt) {
        return atgarder.some(x => x.type === EntityType.virkeskopsAtgardSkogsvard
            && (x.kostnadstyp === Kostnadstyper.DirektAvverkningskostnad || kontrakt.isSkogsvardsKontrakt));
    }
}
